import type { MessageGroup, ResultGroup, ThinkingGroup, ToolGroup } from "../messageGroups"

export function groupOpenCodeProviderEvents(messages: unknown[]): MessageGroup[] {
    const groups: MessageGroup[] = []
    const lastPartIndex = new Map<string, number>()
    const lastMessageIndex = new Map<string, number>()
    const messageRoles = new Map<string, string>()

    for (let index = 0; index < messages.length; index++) {
        const event = openCodeEvent(messages[index])
        if (!event) continue
        if (event.type === "message.updated") {
            const info = recordValue(event.properties.info)
            const id = stringValue(info?.id)
            if (id) {
                lastMessageIndex.set(id, index)
                const role = stringValue(info?.role)
                if (role) messageRoles.set(id, role)
            }
        }
        if (event.type === "message.part.updated") {
            const part = recordValue(event.properties.part)
            const id = stringValue(part?.id)
            if (id) lastPartIndex.set(id, index)
        }
    }

    for (let index = 0; index < messages.length; index++) {
        const raw = recordValue(messages[index])
        if (raw?.type === "session.started") {
            groups.push({ type: "system", subtype: "init", metadata: raw, messageIndex: index })
            continue
        }
        const event = openCodeEvent(messages[index])
        if (!event) continue

        if (event.type === "message.part.updated") {
            const part = recordValue(event.properties.part)
            if (!part) continue
            const id = stringValue(part.id)
            if (id && lastPartIndex.get(id) !== index) continue
            const messageID = stringValue(part.messageID)
            const role = messageID ? messageRoles.get(messageID) : undefined
            switch (stringValue(part.type)) {
                case "text": {
                    if (role === "user" || (!role && !recordValue(part.time))) break
                    const text = stringValue(part.text)
                    if (text) appendText(groups, text, index)
                    break
                }
                case "reasoning": {
                    const text = stringValue(part.text)
                    if (text) appendThinking(groups, text, index)
                    break
                }
                case "tool": {
                    const tool = openCodeToolGroup(part, index)
                    if (tool) groups.push(tool)
                    break
                }
            }
            continue
        }

        if (event.type === "message.updated") {
            const info = recordValue(event.properties.info)
            if (!info || stringValue(info.role) !== "assistant") continue
            const id = stringValue(info.id)
            if (id && lastMessageIndex.get(id) !== index) continue
            const error = recordValue(info.error)
            if (error) {
                groups.push(openCodeErrorResult(error, index))
                continue
            }
            const time = recordValue(info.time)
            if (typeof time?.completed !== "number" || stringValue(info.finish) === "tool-calls") continue
            groups.push(openCodeResult(info, index))
            continue
        }

        if (event.type === "session.error") {
            groups.push(openCodeErrorResult(event.properties.error, index))
        }
    }

    return groups
}

interface OpenCodeEvent {
    type: string
    properties: Record<string, unknown>
}

function openCodeEvent(value: unknown): OpenCodeEvent | null {
    const record = recordValue(value)
    const type = stringValue(record?.type)?.replace(/\.1$/, "")
    if (!type) return null
    const properties = recordValue(record?.properties)
    if (properties) return { type, properties }
    if (type === "message.updated" || type === "message.part.updated" || type === "session.error") return { type, properties: record ?? {} }
    return { type, properties: properties ?? {} }
}

function openCodeToolGroup(part: Record<string, unknown>, messageIndex: number): ToolGroup | null {
    const state = recordValue(part.state)
    const callID = stringValue(part.callID) ?? stringValue(part.id)
    if (!state || !callID) return null
    const status = stringValue(state.status)
    const error = stringValue(state.error)
    const output = stringValue(state.output)
    return {
        type: "tool",
        toolUseId: callID,
        toolName: stringValue(part.tool) ?? stringValue(state.title) ?? "Tool",
        input: state.input ?? {},
        ...(output !== undefined ? { result: output } : error !== undefined ? { result: error } : {}),
        isError: status === "error",
        messageIndices: [messageIndex, status === "completed" || status === "error" ? messageIndex : undefined],
    }
}

function openCodeResult(info: Record<string, unknown>, messageIndex: number): ResultGroup {
    const tokens = recordValue(info.tokens)
    const cache = recordValue(tokens?.cache)
    const time = recordValue(info.time)
    const started = numberValue(time?.created) ?? 0
    const completed = numberValue(time?.completed) ?? started
    return {
        type: "result",
        subtype: "success",
        durationMs: Math.max(0, completed - started),
        totalCostUsd: numberValue(info.cost) ?? 0,
        usage: {
            inputTokens: numberValue(tokens?.input) ?? 0,
            outputTokens: numberValue(tokens?.output) ?? 0,
            ...(numberValue(cache?.read) !== undefined ? { cacheReadTokens: numberValue(cache?.read) } : {}),
            ...(numberValue(cache?.write) !== undefined ? { cacheWriteTokens: numberValue(cache?.write) } : {}),
        },
        isError: false,
        messageIndex,
    }
}

function openCodeErrorResult(error: unknown, messageIndex: number): ResultGroup {
    const record = recordValue(error)
    const data = recordValue(record?.data)
    const message = stringValue(data?.message) ?? stringValue(record?.message) ?? stringValue(record?.name) ?? stringify(error) ?? "OpenCode turn failed"
    return {
        type: "result",
        subtype: "error_during_execution",
        durationMs: 0,
        totalCostUsd: 0,
        usage: { inputTokens: 0, outputTokens: 0 },
        isError: true,
        errors: [message],
        messageIndex,
    }
}

function appendText(groups: MessageGroup[], text: string, messageIndex: number): void {
    const previous = groups.at(-1)
    if (previous?.type === "text") {
        previous.text += text
        previous.endMessageIndex = messageIndex
        return
    }
    groups.push({ type: "text", text, messageIndex })
}

function appendThinking(groups: MessageGroup[], text: string, messageIndex: number): void {
    const previous = groups.at(-1)
    if (previous?.type === "thinking") {
        previous.text += text
        previous.endMessageIndex = messageIndex
        return
    }
    const group: ThinkingGroup = { type: "thinking", text, messageIndex }
    groups.push(group)
}

function stringify(value: unknown): string | undefined {
    if (value === undefined || value === null) return undefined
    if (typeof value === "string") return value
    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}
